import React, { useEffect, useState, useRef } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { getDoc, getDocs, setDoc, collection, doc } from "firebase/firestore";
import { db } from "../../firebase";
import "./BookingPage.css";
import {
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Container,
  Snackbar,
  Alert,
  AlertTitle,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Box,
} from "@mui/material";
import { AccessTime, Euro, Info } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import BookingCalendar from "../../components/bookingCalendar/BookingCalendar";
import {
  formatDateRange,
  weekdayKeys,
} from "../../components/bookingCalendar/calendarUtils";
import CustomPhoneInput from "components/CustomPhoneInput";

// Format a date object to a string in the format YYYY-MM-DD in local timezone
const formatLocalDateKey = (date) => {
  if (!date) return "";
  const offset = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offset.toISOString().split("T")[0];
};

const BookingCalendarPage = ({ homePage = false }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [isLoading, setIsLoading] = useState(true);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [availableWeekdays, setAvailableWeekdays] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [phoneError, setPhoneError] = useState(true);
  const phoneInputRef = useRef(null);
  const [showForm, setShowForm] = useState(false);
  const [highlightedStartDate, setHighlightedStartDate] = useState(null);
  const [highlightedEndDate, setHighlightedEndDate] = useState(null);

  // Add form section reference
  const formSectionRef = useRef(null);

  // Use MUI theme for responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const unavailableSnap = await getDocs(
        collection(db, "bus_unavailable_dates")
      );
      setUnavailableDates(unavailableSnap.docs.map((doc) => doc.id));

      // Updated logic for bookings that have startDate and endDate
      const bookingsSnap = await getDocs(collection(db, "bus_bookings"));
      const bookedDatesList = [];

      bookingsSnap.forEach((doc) => {
        const bookingData = doc.data();

        // Extract startDate and endDate
        if (bookingData.startDate && bookingData.endDate) {
          const startDateObj = bookingData.startDate.toDate
            ? bookingData.startDate.toDate()
            : new Date(bookingData.startDate);

          const endDateObj = bookingData.endDate.toDate
            ? bookingData.endDate.toDate()
            : new Date(bookingData.endDate);

          // Generate all dates in the range
          const currentDate = new Date(startDateObj);
          while (currentDate <= endDateObj) {
            const dateKey = formatLocalDateKey(currentDate);
            bookedDatesList.push(dateKey);
            currentDate.setDate(currentDate.getDate() + 1);
          }
        }
      });

      setBookedDates(bookedDatesList);

      const weekdaysSnap = await getDoc(
        doc(db, "bus_available_weekdays", "bus_available_weekdays")
      );
      if (weekdaysSnap.exists()) {
        setAvailableWeekdays(weekdaysSnap.data());
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      setSnackbar({
        open: true,
        message: "Erreur lors du chargement des disponibilités",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Add this function to check if the selected range contains unavailable dates
  const checkRangeValidity = (start, end) => {
    if (!start || !end) return true;

    const startObj = new Date(start);
    const endObj = new Date(end);

    const currentDate = new Date(startObj);
    while (currentDate <= endObj) {
      const dateKey = formatLocalDateKey(currentDate);
      const dayOfWeek = currentDate.getDay();
      const weekdayName = weekdayKeys[dayOfWeek];

      // Check if date is unavailable or not an allowed weekday
      if (
        unavailableDates.includes(dateKey) ||
        bookedDates.includes(dateKey) ||
        !availableWeekdays[weekdayName] // Check using the weekday name directly
      ) {
        return false;
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return true;
  };

  // Update handleDateChange to check range validity
  const handleDateChange = (start, end, startKey, endKey) => {
    // Only proceed if both dates are selected
    if (!start || !end) return;

    setHighlightedStartDate(start);
    setHighlightedEndDate(end);

    // Store the formatted date keys
    const formattedStartKey = startKey || formatLocalDateKey(start);
    const formattedEndKey = endKey || formatLocalDateKey(end);

    // Set the dates for the booking
    setStartDate(formattedStartKey);
    setEndDate(formattedEndKey);

    // Show the booking form
    setShowForm(true);

    // Scroll to form on mobile
    if (isMobile && formSectionRef.current) {
      const offsetPosition = formSectionRef.current.offsetTop - 50;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const formatDisplayDateRange = () => {
    return formatDateRange(
      highlightedStartDate || (startDate ? new Date(startDate) : null),
      highlightedEndDate || (endDate ? new Date(endDate) : null)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsFormLoading(true);
    const isPhoneValid = phoneInputRef.current?.validate?.() ?? true;
    if (
      !formData.name ||
      !formData.email ||
      !formData.phone ||
      !startDate ||
      !endDate ||
      !isPhoneValid
    ) {
      setSnackbar({
        open: true,
        message: !isPhoneValid
          ? "Le numéro de téléphone n'est pas valide."
          : "Veuillez remplir tous les champs obligatoires et sélectionner une période",
        severity: "error",
      });
      setIsFormLoading(false);
      return;
    }

    // Prevent submission if the range is invalid
    if (!checkRangeValidity(startDate, endDate)) {
      setSnackbar({
        open: true,
        message: "La période sélectionnée contient des dates non disponibles",
        severity: "error",
      });
      return;
    }

    try {
      const docId = `${startDate}_${endDate}_${formData.email}`;
      await setDoc(doc(db, "bus_bookings", docId), {
        ...formData,
        isApproved: false,
        startDate: new Date(`${startDate}T00:00:00`),
        endDate: new Date(`${endDate}T00:00:00`),
        createdAt: new Date(),
      });

      await fetch("https://tourbuslive.fr/contact-form/send_booking.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Site-Secret": process.env.REACT_APP_CONTACT_SECRET,
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          startDate,
          endDate,
          notes: formData.notes,
        }),
      });

      setShowForm(false);
      await fetchData();
      handleCancel();
      setSnackbar({
        open: true,
        message:
          "Réservation enregistrée ! Nous vous contacterons bientôt pour confirmer.",
        severity: "success",
      });
      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-17126121981/-bmyCOf7-tcaEP3Dr-Y_",
          value: 1.0,
          currency: "EUR",
        });
      }
    } catch (error) {
      setIsFormLoading(false);
      console.error("Error submitting booking:", error);
      setSnackbar({
        open: true,
        message: "Erreur lors de l'enregistrement de votre réservation",
        severity: "error",
      });
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setStartDate(null);
    setEndDate(null);
    setHighlightedStartDate(null);
    setHighlightedEndDate(null);

    setFormData({ name: "", email: "", phone: "", notes: "" });
  };

  return (
    <div className="booking-page">
      {!homePage && (
        <div className="booking-hero">
          <h1>Réservation</h1>
          <p>Réservez notre bus de tournée pour votre prochain projet</p>
        </div>
      )}

      <Container maxWidth="lg" className="booking-container">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} className="calendar-section">
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                <CircularProgress color="error" />
              </Box>
            ) : (
              <BookingCalendar
                homePage={homePage}
                unavailableDates={unavailableDates}
                bookedDates={bookedDates}
                availableWeekdays={availableWeekdays}
                onDateChange={handleDateChange}
                highlightedStartDate={highlightedStartDate}
                highlightedEndDate={highlightedEndDate}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6} className="info-section">
            <Paper
              elevation={!homePage ? 3 : 0}
              className={!homePage && "info-paper"}
            >
              <div className="animation-container" ref={formSectionRef}>
                <AnimatePresence mode="wait" initial={false}>
                  {!showForm ? (
                    <motion.div
                      key="info-content"
                      className="info-content"
                      initial={{ x: showForm ? 300 : -300, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: -300, opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                    >
                      <Typography variant="h5" className="section-title">
                        <Info className="section-icon" /> Location du bus de
                        tournée
                      </Typography>

                      <Typography
                        variant="body1"
                        paragraph
                        className="info-text"
                      >
                        {!homePage
                          ? "Notre bus de tournée est disponible à la location pour des événements privés, tournages, loges mobiles, transport de groupes ou hébergement itinérant. Nous proposons des formules flexibles adaptées à vos besoins spécifiques."
                          : "Notre bus de tournée est disponible pour événements privés, tournages et transport de groupes."}
                      </Typography>

                      <Typography variant="h6" className="subsection-title">
                        <AccessTime className="subsection-icon" />{" "}
                        {!homePage
                          ? "Exemples de formules possibles :"
                          : "Exemples :"}
                      </Typography>

                      <ul className="formulas-list">
                        <li>
                          {homePage
                            ? "Soirées privées : karaoké, poker, team building"
                            : "Soirées thématiques privées (karaoké, poker, team building) : 3-4 heures avec ambiance festive"}
                        </li>
                        <li>
                          {homePage
                            ? "Transport événements : concerts, festivals, matchs"
                            : "Transport pour événements (concerts, festivals, événements sportifs) : aller-retour avec confort"}
                        </li>
                        <li>
                          {homePage
                            ? "Tournages : loge mobile, décor authentique"
                            : "Tournages et shoots : loge mobile, décor authentique pour clips, photos ou films"}
                        </li>
                        <li>
                          "Hébergement mobile : solution d'hébergement atypique
                          pour groupes"
                        </li>
                      </ul>

                      <Typography variant="h6" className="subsection-title">
                        <Euro className="subsection-icon" /> Tarification
                      </Typography>

                      <Typography
                        variant="body1"
                        paragraph
                        className="info-text"
                      >
                        {homePage
                          ? "À partir de 140€/personne pour les événements de groupe.\nTarifs sur mesure selon pour les Soirées Privées à partir de 700-900€ selon la durée."
                              .split("\n")
                              .map((line, i) => (
                                <React.Fragment key={i}>
                                  {line}
                                  <br />
                                </React.Fragment>
                              ))
                          : `Transport événements de groupe: à partir de 140€ par personne.
 Soirées privées: à partir de 700-900€ selon la durée.
Tournages et projets spéciaux : tarifs sur devis personnalisé.`
                              .split("\n")
                              .map((line, i) => (
                                <React.Fragment key={i}>
                                  {line}
                                  <br />
                                </React.Fragment>
                              ))}
                      </Typography>

                      <Typography variant="body2" className="info-instructions">
                        {homePage
                          ? "Sélectionnez vos dates pour faire une demande de réservation."
                          : "Sélectionnez une date de début et une date de fin sur le calendrier pour faire une demande de réservation du bus"}
                      </Typography>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="form-content"
                      className="form-content"
                      initial={{ x: 300, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: 300, opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                    >
                      {!checkRangeValidity(startDate, endDate) ? (
                        // Enhanced invalid range warning that replaces the form
                        <div className="invalid-range-container">
                          <Alert
                            severity="error"
                            variant="filled"
                            sx={{
                              mb: 3,
                              fontSize: "1.1rem",
                              "& .MuiAlert-icon": {
                                fontSize: "2rem",
                              },
                            }}
                          >
                            <AlertTitle
                              sx={{ fontSize: "1.3rem", fontWeight: "bold" }}
                            >
                              Période non disponible
                            </AlertTitle>
                            La période sélectionnée contient une ou plusieurs
                            dates non disponibles.
                          </Alert>

                          <Typography
                            variant="h6"
                            sx={{ mb: 2, color: "#555" }}
                          >
                            Pour la période: {formatDisplayDateRange()}
                          </Typography>

                          <Box sx={{ mb: 3 }}>
                            <Typography
                              variant="body1"
                              paragraph
                              sx={{ fontWeight: 500 }}
                            >
                              Impossible de réserver cette période car:
                            </Typography>
                            <ul className="invalid-reasons">
                              <li>
                                Certaines dates sont déjà réservées par d'autres
                                clients
                              </li>
                              <li>
                                Certaines dates ont été marquées comme
                                indisponibles
                              </li>
                              <li>
                                Certains jours de la semaine ne sont pas
                                disponibles à la réservation
                              </li>
                            </ul>
                          </Box>

                          <Typography variant="body1" paragraph>
                            Veuillez revenir au calendrier et sélectionner une
                            autre période disponible (en rouge).
                          </Typography>

                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => handleCancel()}
                            fullWidth
                            sx={{ mt: 2, py: 1.5 }}
                          >
                            Revenir au calendrier
                          </Button>
                        </div>
                      ) : (
                        // Original form when range is valid
                        <>
                          <Typography variant="h5" className="section-title">
                            Réserver pour la période: {formatDisplayDateRange()}
                          </Typography>

                          <form
                            onSubmit={handleSubmit}
                            className="booking-form"
                          >
                            <TextField
                              fullWidth
                              margin="normal"
                              label="Nom complet"
                              value={formData.name}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  name: e.target.value,
                                }))
                              }
                              required
                              variant="outlined"
                            />
                            <TextField
                              fullWidth
                              margin="normal"
                              label="Email"
                              type="email"
                              value={formData.email}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  email: e.target.value,
                                }))
                              }
                              required
                              variant="outlined"
                            />
                            <CustomPhoneInput
                              ref={phoneInputRef}
                              required
                              value={formData.phone}
                              setPhoneError={setPhoneError}
                              onChange={(phone) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  phone: phone,
                                }))
                              }
                              // error={errors.telephone?.message}
                              label="N° de téléphone"
                              placeholder="Entrez votre numéro de téléphone"
                              sx={{ width: "100%" }}
                            />
                            <TextField
                              fullWidth
                              margin="normal"
                              label="Détails de votre projet (nombre de personnes, durée, besoins spécifiques...)"
                              value={formData.notes}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  notes: e.target.value,
                                }))
                              }
                              multiline
                              rows={4}
                              variant="outlined"
                            />

                            <div className="form-actions">
                              <Button
                                variant="outlined"
                                onClick={() => handleCancel()}
                                className="cancel-button"
                              >
                                Annuler
                              </Button>
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                className="submit-button"
                                disabled={isFormLoading}
                                sx={{
                                  minWidth: "180px",
                                  position: "relative",
                                  opacity: isFormLoading ? 0.7 : 1,
                                }}
                              >
                                {isFormLoading ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  "Envoyer ma demande"
                                )}
                              </Button>
                            </div>
                          </form>
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BookingCalendarPage;
